import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { fetchRecordsByPrice } from '../services/SubscriptionItemsService';

// FNP-331: cross-subscription "items by price" read. Mirrors useSubscriptionItems
// but hits the price-keyed read path. Used by the prices detail page Subscriptions
// tab (and any future per-price feed). Module-scoped queryKey prefix prevents
// invalidation crossover with the subscription-keyed listing.
export const useSubscriptionItemsByPrice = (priceId: string) => {
    if (!priceId) {
        throw new Error('priceId is required parameter');
    }

    // queryClient stays so cache invalidation hooks plug in identically to the
    // subscription-keyed sibling, even though this hook is currently read-only.
    void useQueryClient();
    const [keyword, setKeyword] = useState('');
    const [limit] = useState(10);

    const sort = { column: 'created', direction: 'desc' as const };

    const subscriptionItemsQuery = useInfiniteQuery({
        queryKey: ['subscription-items', 'by-price', priceId, keyword, limit, sort.column, sort.direction],
        queryFn: ({ pageParam }: { pageParam: string | null }) =>
            fetchRecordsByPrice(priceId, { limit, sort, nextToken: pageParam }),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null
    });

    const fetchRecordsAction = () => {
        if (subscriptionItemsQuery.hasNextPage) {
            subscriptionItemsQuery.fetchNextPage();
        }
    };

    const handleFilterByKeyword = (newKeyword: string) => {
        setKeyword(newKeyword);
    };

    const allItems = subscriptionItemsQuery.data?.pages?.flatMap(
        page => page && 'items' in page && page.items ? page.items : []
    ) || [];

    return {
        items: allItems,
        visibleItems: allItems,
        loading: subscriptionItemsQuery.isLoading,
        keyword,
        visibleCount: allItems.length,
        totalCount: allItems.length,
        nextToken: subscriptionItemsQuery.hasNextPage ? 'has-more' : null,
        fetchRecords: fetchRecordsAction,
        loadMoreRecords: fetchRecordsAction,
        handleFilterByKeyword
    };
};
